/**
 * Payment section for the native inline checkout — mirrors the GHL accordion:
 *
 *   Express checkout  (Apple/Google Pay, Link, Amazon Pay — wallets only)
 *   ─────────── OR ───────────
 *   Payment   ·  All transactions are secure and encrypted.
 *   ┌─────────────────────────────────────────┐
 *   │ ◉ Credit card            VISA  MC  AMEX +5 │  ← Stripe PaymentElement (card)
 *   │   [ card number / exp / cvc / name ]       │
 *   │ ○ PayPal                          PayPal   │  ← "redirected to PayPal" note
 *   └─────────────────────────────────────────┘
 *   [ Complete Secure Order — $X ]  ← swaps to the blue "Pay with PayPal" button
 *   🔒 SECURE SSL ENCRYPTED   🛡 TRUSTED SELLER
 *
 * Card + PayPal are radio siblings (NOT a checkbox toggle). ONE action button
 * below swaps by selected method: Stripe confirmPayment for card, the PayPal
 * SDK button (rendered via renderPayPal) for PayPal. Rendered inside <Elements>;
 * both Stripe confirm paths mirror _checkout-form.tsx so the money path is
 * identical to /checkout. PayPal stays on our own provider (PayPalSection).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ExpressCheckoutElement, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { friendlyPaymentError } from "@/lib/payment-error";
import { InstallmentsMessaging } from "./InstallmentsMessaging";
import { expressCheckoutPaymentMethodsFor } from "./express-checkout-payment-methods";
import { paymentElementDefaultValuesFor } from "./payment-element-defaults";
import { paymentMethodOrderFor } from "./payment-method-order";

/** Imperative handle the parent uses to push add-on-driven updates INTO this
 *  component. @stripe/react-stripe-js's <Elements> provider does not reliably
 *  commit parent-driven prop changes to its subtree's DOM (a parent re-render
 *  updates our props but React skips the host mutation + passive effects until a
 *  state change ORIGINATES on this fiber). So instead of prop→effect, the parent
 *  calls these setters from its own (reliably-running) effects; the setState then
 *  originates here and commits. See use-checkout-inline / CheckoutInline. */
export type PaymentBlockApi = {
  /** DISPLAY-currency minor units (totalDisplayCents — GBP/ZAR/… target cents,
   *  NOT USD). fmt is fmtTarget and formats without converting; passing USD
   *  cents here re-creates the one-number-conversion class (£48-button bug). */
  setTotal: (targetCents: number) => void;
  setSyncing: (v: boolean) => void;
  refresh: () => void;
};

function Radio({ checked }: { checked: boolean }) {
  return (
    <span className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 ${checked ? "border-teal-600" : "border-gray-300"}`}>
      {checked && <span className="h-2 w-2 rounded-full bg-teal-600" />}
    </span>
  );
}

function CardBrands() {
  return (
    <span className="flex items-center gap-1">
      <span className="rounded bg-[#1434CB] px-1 py-0.5 text-[8px] font-bold italic leading-none text-white">VISA</span>
      <span className="flex h-3.5 items-center rounded bg-white px-0.5 shadow-sm ring-1 ring-gray-200">
        <span className="h-2.5 w-2.5 rounded-full bg-[#EB001B]" />
        <span className="-ml-1 h-2.5 w-2.5 rounded-full bg-[#F79E1B]" />
      </span>
      <span className="rounded bg-[#1F72CD] px-1 py-0.5 text-[8px] font-bold leading-none text-white">AMEX</span>
      <span className="text-[10px] font-semibold text-blue-600">+5</span>
    </span>
  );
}

function PayPalMark() {
  return (
    <span className="text-sm font-bold italic leading-none">
      <span className="text-[#003087]">Pay</span><span className="text-[#009cde]">Pal</span>
    </span>
  );
}

export function PaymentBlock({
  email,
  firstName,
  lastName,
  billingCountry,
  orderId,
  totalCents,
  fmt,
  onPaymentInitiated,
  trackFunnel,
  paypalAvailable,
  renderPayPal,
  ensureVault,
  confirmWithToken,
  syncing = false,
  amountApiRef,
  installmentsEnabled = false,
  installmentsFirst = false,
  chargeCurrency,
  chargeTotalCents,
}: {
  email: string;
  firstName: string;
  lastName: string;
  billingCountry: string;
  orderId: string;
  /** Display total in ACTIVE-currency minor units (per-line: catalog plan +
   *  FX add-ons) — the same figure the summary lines sum to. Display-only;
   *  the charge amount lives on the PI (server-authoritative). */
  totalCents: number;
  /** Formats ACTIVE-currency minor units — must NOT convert again. */
  fmt: (targetCents: number) => string;
  onPaymentInitiated?: () => void;
  /** First-party funnel pipe (useIslandFunnel). Card + express emit
   *  payment_method_selected / payment_initiated through it; PayPal is excluded
   *  because PayPalSection posts both itself. */
  trackFunnel?: (type: string, data?: Record<string, unknown>) => void;
  paypalAvailable: boolean;
  /** Initial value of the in-flight re-price flag (kept in sync afterwards via
   *  amountApiRef.setSyncing). While true the pay button is disabled so a click
   *  can't confirm the PI at the pre-PATCH amount. */
  syncing?: boolean;
  /** The parent assigns this ref to an imperative API (see PaymentBlockApi) and
   *  drives the add-on total / syncing flag / element-refresh through it, because
   *  prop-driven updates don't commit inside <Elements> (see PaymentBlockApi). */
  amountApiRef?: { current: PaymentBlockApi | null };
  /** The PayPal SDK button (PayPalSection), supplied by the parent so this
   *  component stays free of PayPal wiring. Shown when PayPal is selected. */
  renderPayPal: () => ReactNode;
  /** Eager path: attaches a Stripe Customer + setup_future_usage to the PI
   *  (vaults the card) so the one-click upsell can charge it. Before confirmPayment. */
  ensureVault?: (email: string) => Promise<void>;
  /** ctMode (ConfirmationToken prototype): when provided, card + express skip
   *  the eager confirmPayment and instead createConfirmationToken → POST it so
   *  the server mints+confirms the PI and vaults the card server-side (mandate-
   *  free). Returns the PI status; this component handles 3DS via handleNextAction. */
  confirmWithToken?: (
    confirmationTokenId: string,
    overrideEmail?: string,
  ) => Promise<{ status: string; client_secret?: string; error?: string; decline_code?: string }>;
  /** publicConfig.installments_enabled (CHECKOUT_INSTALLMENTS_ENABLED). Off →
   *  no messaging element and NO paymentMethodOrder, i.e. byte-for-byte the
   *  behaviour this component had before installments existed. */
  installmentsEnabled?: boolean;
  /** The buyer arrived via an LP "or pay in monthly installments" control, so
   *  put Klarna/Afterpay first in the Payment Element. Ordering ONLY - we never
   *  pass paymentMethodTypes, because dynamic payment methods must keep
   *  deciding eligibility (an allowlist would show ineligible tabs and hide
   *  methods Stripe enabled later). Card is always one click away. */
  installmentsFirst?: boolean;
  /** CHARGE currency + amount (minor units) for the messaging element. NOT the
   *  display figures: for a South-Africa buyer those are ZAR and the charge is
   *  USD, and advertising instalments on money that is never charged would be
   *  a second, wrong money surface. */
  chargeCurrency?: string;
  chargeTotalCents?: number;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [method, setMethod] = useState<"card" | "paypal">("card");
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [expressState, setExpressState] = useState<"loading" | "available" | "none">("loading");
  // Scroll a new payment error into view — it renders between the accordion and
  // the pay button and is easy to miss after a wallet-sheet / 3DS round-trip.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (errorMsg) errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [errorMsg]);

  // Funnel events fire at most once per key (a retried payment must not inflate
  // the step). Mirrors trackOnce in checkout/[slug]/_checkout-form.tsx.
  const funnelFired = useRef<Set<string>>(new Set());
  function trackFunnelOnce(key: string, type: string, data: Record<string, unknown>) {
    // Only burn the key once a sender exists — marking it while trackFunnel is
    // absent would suppress the event for the rest of the session.
    if (!trackFunnel || funnelFired.current.has(key)) return;
    funnelFired.current.add(key);
    trackFunnel(type, data);
  }
  const selectMethod = (m: "card" | "paypal") => {
    setMethod(m);
    // PayPal excluded: PayPalSection posts payment_method_selected itself at the
    // button click, which is the real selection moment for that path.
    if (m === "card") trackFunnelOnce("method:card", "payment_method_selected", { method: "card", mode: "island" });
  };

  // Add-on-driven display state lives in LOCAL state (initialised from props) and
  // is updated by the parent via amountApiRef — NOT via a prop→effect — because
  // <Elements> doesn't commit parent-driven updates to this subtree (the button
  // would freeze at the first amount, e.g. "$47" while the real total is $90, and
  // the "Updating…"/disabled state would never paint). A setState that originates
  // on THIS fiber commits, so the parent calls these setters from its own
  // reliably-running effects. refresh() pulls the new amount into the Elements so
  // the express wallet sheet charges the correct total.
  const [liveTotal, setLiveTotal] = useState(totalCents);
  const [liveSyncing, setLiveSyncing] = useState(syncing);
  if (amountApiRef) {
    amountApiRef.current = {
      setTotal: setLiveTotal,
      setSyncing: setLiveSyncing,
      refresh: () => { if (elements) void elements.fetchUpdates(); },
    };
  }

  const returnUrl = () => `${window.location.origin}/checkout/upsell?order_id=${orderId}`;

  // ctMode confirm: validate/collect → createConfirmationToken (no SFU on the
  // Elements → no mandate) → server mints+confirms the PI + vaults server-side →
  // handle 3DS inline → redirect to the upsell. Returns true when it handled the
  // flow (success or error shown); false to fall through to the eager path.
  async function payWithConfirmationToken(overrideEmail?: string): Promise<void> {
    if (!stripe || !elements || !confirmWithToken) return;
    const { error: submitErr } = await elements.submit();
    if (submitErr) {
      setErrorMsg(submitErr.message || "Please check your details.");
      setProcessing(false);
      return;
    }
    const { error: ctErr, confirmationToken } = await stripe.createConfirmationToken({
      elements,
      params: { return_url: returnUrl() },
    });
    if (ctErr || !confirmationToken) {
      setErrorMsg(ctErr?.message || "Payment failed. Please try again.");
      setProcessing(false);
      return;
    }
    const res = await confirmWithToken(confirmationToken.id, overrideEmail);
    if (res.status === "requires_action" && res.client_secret) {
      const { error: naErr } = await stripe.handleNextAction({ clientSecret: res.client_secret });
      if (naErr) {
        setErrorMsg(naErr.message || "Authentication failed. Please try again.");
        setProcessing(false);
        return;
      }
      window.location.href = returnUrl();
      return;
    }
    if (res.status === "succeeded" || res.status === "processing" || res.status === "requires_capture") {
      window.location.href = returnUrl();
      return;
    }
    // Server-side confirm failed; decline_code rides along so the buyer gets
    // the same actionable copy as the client-side confirm path.
    setErrorMsg(
      res.decline_code
        ? friendlyPaymentError({ code: "card_declined", decline_code: res.decline_code, message: res.error }, { paypalAvailable })
        : res.error || "Payment failed. Please try again.",
    );
    setProcessing(false);
  }

  async function payCard() {
    if (!stripe || !elements) return;
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setErrorMsg("Please enter your email address.");
      return;
    }
    setProcessing(true);
    setErrorMsg("");
    onPaymentInitiated?.();
    // After validation only: a rejected email is not a payment attempt.
    trackFunnelOnce("method:card", "payment_method_selected", { method: "card", mode: "island" });
    trackFunnelOnce("pi:card", "payment_initiated", { method: "card", mode: "island" });
    if (confirmWithToken) {
      await payWithConfirmationToken(email.trim());
      return;
    }
    // Eager path: vault the card BEFORE confirming (attaches Stripe Customer +
    // setup_future_usage) so the one-click upsell can charge it.
    await ensureVault?.(email.trim());
    const fullName = `${firstName} ${lastName}`.trim();
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl(),
        receipt_email: email || undefined,
        payment_method_data: {
          billing_details: {
            name: fullName || undefined,
            email: email || undefined,
            address: billingCountry ? { country: billingCountry } : undefined,
          },
        },
      },
    });
    if (error) {
      setErrorMsg(friendlyPaymentError(error, { paypalAvailable }));
      setProcessing(false);
    }
  }

  const cardRowSel = method === "card";
  const ppRowSel = method === "paypal";

  return (
    <div>
      {/* Express checkout (wallets) — separate row on top, hidden when none. */}
      <div className={expressState === "none" ? "hidden" : "mb-4"}>
        {expressState !== "none" && <p className="mb-2 text-center text-sm text-gray-500">Express checkout</p>}
        <div style={{ minHeight: expressState === "available" ? undefined : 44 }}>
          <ExpressCheckoutElement
            onReady={({ availablePaymentMethods }) =>
              setExpressState(
                availablePaymentMethods && Object.values(availablePaymentMethods).some(Boolean) ? "available" : "none",
              )
            }
            onConfirm={async (event) => {
              if (!stripe || !elements) return;
              setProcessing(true);
              setErrorMsg("");
              onPaymentInitiated?.();
              const expressMethod = (event as { expressPaymentType?: string }).expressPaymentType || "wallet";
              trackFunnelOnce("method:" + expressMethod, "payment_method_selected", { method: expressMethod, mode: "island" });
              trackFunnelOnce("pi:express", "payment_initiated", { method: expressMethod, mode: "island" });
              const walletEmail = event.billingDetails?.email;
              const buyerEmail = email.trim() || walletEmail || undefined;
              const buyerName = `${firstName} ${lastName}`.trim() || event.billingDetails?.name || undefined;
              const country = billingCountry || event.billingDetails?.address?.country || undefined;
              if (confirmWithToken) {
                await payWithConfirmationToken(buyerEmail);
                return;
              }
              // Eager path: vault the wallet card so the one-click upsell can charge it.
              if (buyerEmail) await ensureVault?.(buyerEmail);
              const { error } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                  return_url: returnUrl(),
                  receipt_email: buyerEmail,
                  payment_method_data: {
                    billing_details: { name: buyerName, email: buyerEmail, address: country ? { country } : undefined },
                  },
                },
              });
              if (error) {
                setErrorMsg(friendlyPaymentError(error, { paypalAvailable }));
                setProcessing(false);
              }
            }}
            options={{
              buttonType: { applePay: "buy", googlePay: "buy" },
              layout: { overflow: "never" },
              emailRequired: true,
              billingAddressRequired: true,
              // With instalments ON, Klarna is kept OUT of this express row so
              // it stays a Payment Element tab next to Card/Afterpay, under the
              // instalments messaging. See express-checkout-payment-methods.ts.
              // Off means no key at all. Other wallets are untouched.
              ...(() => {
                const paymentMethods = expressCheckoutPaymentMethodsFor(installmentsEnabled);
                return paymentMethods ? { paymentMethods } : {};
              })(),
            }}
          />
        </div>
        {expressState === "available" && (
          <div className="my-4 flex items-center gap-3 text-xs text-gray-400">
            <span className="h-px flex-1 bg-gray-200" /> OR <span className="h-px flex-1 bg-gray-200" />
          </div>
        )}
      </div>

      {/* Card / PayPal radio accordion — each method is its own rounded card so
          the corners stay correct; the selected card gets the teal frame (same
          teal as the checkout button). border-2 on both states → no 1px shift. */}
      <div className="space-y-2.5">
        {/* Credit card */}
        <div className={`overflow-hidden rounded-xl border-2 ${cardRowSel ? "border-teal-600 bg-teal-50/40" : "border-gray-200 bg-white"}`}>
          <button type="button" onClick={() => selectMethod("card")} className="flex w-full items-center justify-between bg-transparent px-4 py-3.5">
            <span className="flex items-center gap-2.5"><Radio checked={cardRowSel} /><span className="text-sm font-semibold text-gray-900">Credit card</span></span>
            <CardBrands />
          </button>
          {/* Keep PaymentElement mounted; just hide its body when PayPal is active. */}
          <div className={cardRowSel ? "px-4 pb-4" : "hidden"}>
            <InstallmentsMessaging
              enabled={installmentsEnabled}
              amountCents={chargeTotalCents || 0}
              currency={chargeCurrency || ""}
              countryCode={billingCountry}
            />
            <PaymentElement
              options={{
                layout: "tabs",
                terms: { card: "never" },
                // Whenever instalments are ON we state the tab order, because
                // Stripe's own order hid Klarna/Afterpay in the mobile overflow.
                // Ordering only; see payment-method-order.ts. Off means no key.
                ...(() => {
                  const order = paymentMethodOrderFor(installmentsEnabled, installmentsFirst);
                  return order ? { paymentMethodOrder: order } : {};
                })(),
                // Klarna eligibility is country-dependent, so an element that
                // does not know the buyer's country parks Klarna last whatever
                // order we passed. Seed the geo-prefilled country at creation;
                // it is an initial value, so a country the buyer picks wins.
                // Off (or country unknown) means no key at all.
                ...(() => {
                  const defaultValues = paymentElementDefaultValuesFor(installmentsEnabled, billingCountry);
                  return defaultValues ? { defaultValues } : {};
                })(),
              }}
            />
          </div>
        </div>

        {/* PayPal */}
        {paypalAvailable && (
          <div className={`overflow-hidden rounded-xl border-2 ${ppRowSel ? "border-teal-600 bg-teal-50/40" : "border-gray-200 bg-white"}`}>
            <button type="button" onClick={() => selectMethod("paypal")} className="flex w-full items-center justify-between bg-transparent px-4 py-3.5">
              <span className="flex items-center gap-2.5"><Radio checked={ppRowSel} /><span className="text-sm font-semibold text-gray-900">PayPal</span></span>
              <PayPalMark />
            </button>
            {ppRowSel && (
              <div className="px-4 pb-4 text-center text-sm text-gray-600">
                You&apos;ll be redirected to PayPal to complete your purchase
              </div>
            )}
          </div>
        )}
      </div>

      {errorMsg && (
        <div
          ref={errorRef}
          role="alert"
          className="mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-600"
        >
          {errorMsg}
        </div>
      )}

      {/* ONE action button — swaps by selected method */}
      <div className="mt-5">
        {ppRowSel ? (
          renderPayPal()
        ) : (
          <button
            // KEYED on the live display values: inside <Elements>, React commits
            // node add/remove (structural) but NOT text/attr mutations to an
            // existing node (a react-stripe-js v6 + React 19 quirk — proven: the
            // amount only repaints after a PayPal↔Card toggle, which remounts this
            // button). Encoding the values in the key turns each change into a
            // remount, which DOES commit, so the amount + disabled state paint.
            key={`pay-${liveTotal}-${liveSyncing ? 1 : 0}-${processing ? 1 : 0}`}
            type="button"
            disabled={!stripe || processing || liveSyncing}
            onClick={payCard}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-6 py-4 text-base font-semibold text-white transition-colors hover:bg-teal-700 disabled:bg-teal-400"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {processing ? "Processing…" : liveSyncing ? "Updating…" : `Complete Secure Order — ${fmt(liveTotal)}`}
          </button>
        )}
      </div>

      {/* Trust seal — directly under the action button */}
      <div className="mt-5 flex items-center justify-center gap-8 opacity-90">
        <div className="flex items-center gap-2">
          <svg className="text-green-600" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          <div className="leading-none"><div className="text-[10px] font-black text-stone-700">SECURE</div><div className="text-[8px] font-bold text-stone-400">SSL ENCRYPTED</div></div>
        </div>
        <div className="flex items-center gap-2">
          <svg className="text-blue-600" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" /><path d="m9 12 2 2 4-4" /></svg>
          <div className="leading-none"><div className="text-[10px] font-black text-stone-700">TRUSTED</div><div className="text-[8px] font-bold text-stone-400">SELLER</div></div>
        </div>
      </div>
    </div>
  );
}
